/**
 * Notes `source` field convention used by the card schema.
 *
 * A card's `source` may encode "this card's back is rendered from notes
 * document X" as `notes:<method>:<docId>` — or, since a deck can hold several
 * notes, `notes:<method>:<docId>:<noteId>`. These helpers build and parse
 * that string — pure string operations, no editor dependencies.
 *
 * THE NOTE ID IS LOAD-BEARING, not decoration. When a card is deleted, the
 * cleanup pass strips its highlight mark out of the note it came from; with
 * no id in the ref it could only ever open the deck's FIRST note, so deleting
 * a card made from Note 3 quietly rewrote Note 1 — removing highlights the
 * user never touched, in a document they were not looking at.
 *
 * A ref without the fourth segment is a card written before the deck could
 * hold more than one note, and belongs to the default note — the same
 * "unqualified means the first one" rule the document card refs use.
 */

/** The note every ref written before multi-note support belongs to. */
const DEFAULT_NOTE_ID = 'default';

export type NotesMethod = 'manual' | 'generated' | 'image';

export interface ParsedNotesSource {
  method: NotesMethod;
  docId: string;
  /** Resolved: a legacy ref reads as the deck's default note. */
  noteId: string;
}

export function buildNotesSource(
  method: NotesMethod,
  docId: string,
  noteId: string = DEFAULT_NOTE_ID,
): string {
  // The default note keeps the three-segment form, so an older client still
  // parses every ref this one writes for the note it already knows about.
  return noteId === DEFAULT_NOTE_ID
    ? `notes:${method}:${docId}`
    : `notes:${method}:${docId}:${noteId}`;
}

export function parseNotesSource(source: string): ParsedNotesSource | null {
  // A docId is a deck path and can contain colons, so the note id is taken
  // from the END, and only when the tail looks like an id rather than part
  // of the path: ids are minted by `uuid()`.
  const match = source.match(/^notes:(manual|generated|image):(.+)$/);
  if (!match) return null;
  const rest = match[2];
  const tail = rest.lastIndexOf(':');
  if (tail > 0 && UUID.test(rest.slice(tail + 1))) {
    return {
      method: match[1] as NotesMethod,
      docId: rest.slice(0, tail),
      noteId: rest.slice(tail + 1),
    };
  }
  return { method: match[1] as NotesMethod, docId: rest, noteId: DEFAULT_NOTE_ID };
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isNotesSource(source: string | undefined): boolean {
  return !!source?.startsWith('notes:');
}
